import React, { useEffect, useState } from 'react';
import { getUserById } from '../services/userService';
import FollowButton from './FollowButton';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, getDocs, doc, getDoc, onSnapshot, query, collectionGroup, where, orderBy, limit } from 'firebase/firestore';
import useToast from '../hooks/useToast';
import FollowersFollowingModal from './FollowersFollowingModal';

const ListenerProfile = ({ uid }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);
    const [userBadges, setUserBadges] = useState([]); // will be array of { artistId, artistName, badgeTitle, totalTips }
    const [totalTipsGiven, setTotalTipsGiven] = useState(0);
    const [recentGivenTips, setRecentGivenTips] = useState([]);
    const [recentReceivedTips, setRecentReceivedTips] = useState([]); // For artists
    const [artistSongs, setArtistSongs] = useState([]); // For artists
    const [postsCount, setPostsCount] = useState(0);
    const [artistBadgeCounts, setArtistBadgeCounts] = useState({ crown:0, diamond:0, gold:0, silver:0, bronze:0 });
    const [followersCount, setFollowersCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);
    const { toast } = useToast();

    // realtime user doc listener
    useEffect(() => {
        if (!uid) {
            setUser(null);
            return;
        }
        const uRef = doc(db, 'users', uid);
        const unsub = onSnapshot(uRef, (snap) => {
            if (snap.exists()) {
                const userData = { id: snap.id, ...snap.data() };
                setUser(userData);
            } else {
                setUser(null);
            }
        }, (err) => {
            console.error('User snapshot error', err);
            setUser(null);
        });
        return () => unsub();
    }, [uid]);

    // For artist profiles: compute posts count from songs collection and load songs (realtime)
    useEffect(() => {
        if (!uid) return;
        
        // Load count separately (total posts) - one time
        (async () => {
            try {
                const songsCol = collection(db, 'songs');
                // Try artistId first (as saved in ArtistUpload.js)
                const q1 = query(songsCol, where('artistId', '==', uid));
                const s1 = await getDocs(q1);
                if (s1.size > 0) {
                    setPostsCount(s1.size);
                    return;
                }
                // Fallback to userId for backward compatibility
                const q2 = query(songsCol, where('userId', '==', uid));
                const s2 = await getDocs(q2);
                if (s2.size > 0) {
                    setPostsCount(s2.size);
                    return;
                }
                // Fallback to ownerId
                const q3 = query(songsCol, where('ownerId', '==', uid));
                const s3 = await getDocs(q3);
                setPostsCount(s3.size);
            } catch (e) {
                console.warn('Failed to count posts', e);
            }
        })();
        
        // Load songs for display (limited to 10) - realtime
        // Try artistId first
        const songsCol = collection(db, 'songs');
        const q1 = query(songsCol, where('artistId', '==', uid), orderBy('createdAt', 'desc'), limit(10));
        
        const unsub1 = onSnapshot(
            q1,
            (snap) => {
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setArtistSongs(list);
            },
            (err) => {
                console.warn('Failed to load songs by artistId', err);
                setArtistSongs([]);
            }
        );
        
        return () => unsub1();
    }, [uid]);

    // For artists: load received tips (tips received on their songs)
    useEffect(() => {
        if (!uid) return () => {};
        
        let isActive = true;
        
        const q = query(
            collection(db, 'transactions'),
            where('receiverId', '==', uid),
            where('status', '==', 'confirmed')
        );
        const unsub = onSnapshot(q, (snap) => {
            if (!isActive) return;
            
            (async () => {
                const entries = [];
                for (const s of snap.docs) {
                    if (!isActive) return;
                    const tx = { id: s.id, ...s.data() };
                    const sender = tx.senderId ? await getUserById(tx.senderId) : null;
                    const senderName = sender?.name || sender?.displayName || sender?.fullName || sender?.email || tx.senderName || tx.fromName || 'Supporter';
                    const songId = s.ref.parent.parent ? s.ref.parent.parent.id : null;
                    if (songId) {
                        try {
                            const songDoc = await getDoc(doc(db, 'songs', songId));
                            const songData = songDoc.exists() ? { id: songDoc.id, ...songDoc.data() } : null;
                            entries.push({ tx, song: songData, fromName: senderName });
                        } catch (err) {
                            entries.push({ tx, song: null, fromName: senderName });
                        }
                    } else {
                        entries.push({ tx, song: null, fromName: senderName });
                    }
                }
                if (isActive) {
                    entries.sort((a, b) => {
                        const aTime = a.tx.timestamp?.toMillis?.() || a.tx.createdAt?.toMillis?.() || 0;
                        const bTime = b.tx.timestamp?.toMillis?.() || b.tx.createdAt?.toMillis?.() || 0;
                        return bTime - aTime;
                    });
                    setRecentReceivedTips(entries.slice(0, 3));
                }
            })();
        }, (err) => {
            console.error('Received tips snapshot error', err);
            if (isActive) {
                setRecentReceivedTips([]);
            }
        });
        
        return () => {
            isActive = false;
            unsub();
        };
    }, [uid]);

    // Note: Badge logic is now handled by badgeService.js when tips are confirmed
    // This useEffect is kept for backward compatibility but badges are updated via badgeService

    // Artists: live counts of supporters per badge (from listeners' badges where artistId == artist uid)
    useEffect(() => {
        if (!uid) return () => {};
        
        // Query collection group for badges where artistId field matches this artist's uid
        // The document ID in users/{listenerId}/badges/{artistId} is the artistId
        // So we filter by artistId field instead of documentId
        const q = query(collectionGroup(db, 'badges'), where('artistId', '==', uid));
        const unsub = onSnapshot(q, (snap) => {
            const counts = { crown:0, diamond:0, gold:0, silver:0, bronze:0 };
            snap.forEach((d) => {
                const badgeType = d.data()?.badgeType || '';
                const t = badgeType.toLowerCase();
                if (t.includes('crown')) counts.crown += 1;
                else if (t.includes('diamond')) counts.diamond += 1;
                else if (t.includes('gold')) counts.gold += 1;
                else if (t.includes('silver')) counts.silver += 1;
                else if (t.includes('bronze')) counts.bronze += 1;
            });
            setArtistBadgeCounts(counts);
        }, (err) => {
            console.warn('Artist badge counts listener failed', err);
            // If query fails (e.g., no index), set empty counts
            setArtistBadgeCounts({ crown:0, diamond:0, gold:0, silver:0, bronze:0 });
        });
        return () => unsub();
    }, [uid]);

    // realtime badges listener under users/{uid}/badges
    useEffect(() => {
        if (!uid) return () => {};
        
        let isActive = true;
        
        const badgesCol = collection(db, 'users', uid, 'badges');
        const unsub = onSnapshot(badgesCol, (snap) => {
            if (!isActive) return;
            
            (async () => {
                try {
                    const entries = [];
                    let totalTipCount = 0;
                    for (const d of snap.docs) {
                        if (!isActive) return;
                        const data = d.data();
                        const artistId = d.id;
                        let artistName = data.artistName || data.artist || data.artistDisplayName;
                        if (!artistName) {
                            try {
                                const artistUser = await getUserById(artistId);
                                artistName = artistUser?.name || artistUser?.displayName || 'Artist';
                            } catch (e) {
                                console.warn('Failed to fetch artist name for badge:', artistId, e);
                                artistName = 'Artist';
                            }
                        }
                        const badgeType = data.badgeType || 'bronze';
                        const tipCount = Number(data.tipCount) || 0;
                        const level = Number(data.level) || 1;
                        totalTipCount += tipCount;
                        
                        // Create badge title for display
                        const badgeEmojis = {
                            crown: '👑',
                            diamond: '💎',
                            gold: '🥇',
                            silver: '🥈',
                            bronze: '🥉'
                        };
                        const badgeTitle = `${badgeEmojis[badgeType] || '🥉'} ${badgeType.charAt(0).toUpperCase() + badgeType.slice(1)} Fan ${artistName}`;
                        
                        entries.push({ artistId, artistName, badgeType, tipCount, level, badgeTitle });
                    }
                    if (!isActive) return;
                    
                    setUserBadges(entries);
                    // Calculate total tips given (amount) from transactions for supporter level
                    // This is separate from badge count
                    const txnCol = collection(db, 'transactions');
                    const txnQuery = query(txnCol, where('senderId', '==', uid), where('status', '==', 'confirmed'));
                    const txnSnap = await getDocs(txnQuery);
                    let totalAmount = 0;
                    txnSnap.forEach((doc) => {
                        totalAmount += Number(doc.data().amount || 0);
                    });
                    if (isActive) {
                        setTotalTipsGiven(totalAmount);
                    }
                } catch (err) {
                    console.error('Failed to process badges snapshot', err);
                }
            })();
        }, (err) => {
            console.error('Badges snapshot error', err);
            if (isActive) {
                setUserBadges([]);
                setTotalTipsGiven(0);
            }
        });
        
        return () => {
            isActive = false;
            unsub();
        };
    }, [uid]);

    // recent given tips loader (same as before)
    // realtime recent given tips using collectionGroup
    useEffect(() => {
        if (!uid) {
            setRecentGivenTips([]);
            return () => {};
        }
        
        let isActive = true;
        
        const processSnapshot = (snap) => {
            if (!isActive) return;
            
            (async () => {
                const entries = [];
                for (const s of snap.docs) {
                    if (!isActive) return;
                    const tx = { id: s.id, ...s.data() };
                    const songId = s.ref.parent.parent ? s.ref.parent.parent.id : null;
                    if (songId) {
                        try {
                            const songDoc = await getDoc(doc(db, 'songs', songId));
                            const songData = songDoc.exists() ? { id: songDoc.id, ...songDoc.data() } : null;
                            entries.push({ tx, song: songData });
                        } catch (err) {
                            console.error('Failed to fetch song for tx', err);
                            entries.push({ tx, song: null });
                        }
                    } else {
                        entries.push({ tx, song: null });
                    }
                }
                if (isActive) {
                    setRecentGivenTips(entries);
                }
            })();
        };
        
        const q = query(
            collection(db, 'transactions'),
            where('senderId', '==', uid),
            where('status', '==', 'confirmed')
        );
        const unsub = onSnapshot(
            q,
            processSnapshot,
            (err) => {
                console.error('Recent tips snapshot error', err);
                if (isActive) {
                    setRecentGivenTips([]);
                }
            }
        );
        
        return () => {
            isActive = false;
            unsub();
        };
    }, [uid]);

    // Real-time listeners for followers and following counts
    useEffect(() => {
        if (!uid) return () => {};
        
        // Listen to followers subcollection
        const followersCol = collection(db, "users", uid, "followers");
        const unsubFollowers = onSnapshot(followersCol, (snap) => {
            setFollowersCount(snap.size);
        }, (err) => {
            console.error('Error listening to followers:', err);
        });

        // Listen to following subcollection
        const followingCol = collection(db, "users", uid, "following");
        const unsubFollowing = onSnapshot(followingCol, (snap) => {
            setFollowingCount(snap.size);
        }, (err) => {
            console.error('Error listening to following:', err);
        });

        return () => {
            unsubFollowers();
            unsubFollowing();
        };
    }, [uid]);

    // Render immediately, show skeleton if user not loaded yet
    if (!user) {
        return (
            <div className="min-h-screen bg-[#0d0d0d] flex flex-col items-center justify-start">
                <div className="w-full max-w-md mx-auto px-4 overflow-hidden text-white">
                    <div className="flex flex-col items-center mt-8">
                        <div className="h-24 w-24 rounded-full bg-neutral-800 animate-pulse"></div>
                        <div className="h-8 w-48 bg-neutral-800 rounded mt-4 animate-pulse"></div>
                        <div className="h-12 w-full bg-neutral-800 rounded mt-6 animate-pulse"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Compute supporter level based on total tips
    const computeSupporterLevel = (total) => {
        if (total >= 500) return 4;
        if (total >= 100) return 3;
        if (total >= 50) return 2;
        if (total >= 10) return 1;
        return 0;
    };

    const supporterLevel = computeSupporterLevel(totalTipsGiven || 0);
    const isOwnProfile = auth.currentUser && auth.currentUser.uid === uid;
    const displayName = user.name || user.displayName || 'Unknown';

    const isArtist = user?.role === 'artist';

    const stats = [
        {
            label: 'Following',
            value: followingCount,
            onClick: () => setShowFollowingModal(true),
        },
        {
            label: 'Followers',
            value: followersCount,
            onClick: () => setShowFollowersModal(true),
        },
        isArtist
            ? { label: 'Posts', value: postsCount || 0 }
            : { label: 'Tips Given', value: Number(totalTipsGiven || 0).toLocaleString() },
    ];

    return (
        <div className="min-h-screen bg-[#0d0d0d] text-white">
            {toast && <div className="app-toast">{toast}</div>}
            <div className="max-w-md mx-auto px-4 pb-24">
                <div className="flex flex-col items-center pt-10">
                    <div className="relative">
                        <button
                            type="button"
                            disabled={!isOwnProfile}
                            onClick={() => window.dispatchEvent(new CustomEvent('open-profile-photo-editor'))}
                            aria-label="Edit profile picture"
                            className="h-28 w-28 rounded-full overflow-hidden bg-neutral-800 ring-2 ring-neutral-700 shadow-lg flex items-center justify-center border-0 p-0 disabled:cursor-default"
                        >
                            {user.photoURL ? (
                                <img src={user.photoURL} alt={displayName} className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-4xl font-bold text-white">
                                    {displayName.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </button>
                        {isOwnProfile && (
                            <button
                                onClick={() => window.dispatchEvent(new CustomEvent('open-profile-photo-editor'))}
                                title="Change photo"
                                className="absolute -bottom-1 -right-1 h-10 w-10 rounded-full bg-white border-2 border-[#0e0e0e] flex items-center justify-center text-black hover:scale-105 transition cursor-pointer"
                            >
                                📷
                            </button>
                        )}
                    </div>

                    <h1 className="mt-4 text-3xl font-extrabold tracking-wide text-center">{displayName}</h1>
                    {user.username && (
                        <p className="mt-1 text-sm text-white/50">@{user.username}</p>
                    )}

                    {user.bio && (
                        <p className="mt-3 text-center text-white/70 text-sm leading-relaxed max-w-sm">
                            {user.bio}
                        </p>
                    )}

                    <div className="mt-6 w-full rounded-3xl bg-white/5 ring-1 ring-white/10 shadow-lg">
                        <div className="grid grid-cols-3 divide-x divide-white/5">
                            {stats.map((stat, index) => (
                                <button
                                    key={stat.label}
                                    type="button"
                                    onClick={stat.onClick}
                                    className={`flex flex-col items-center justify-center gap-1 py-4 transition ${
                                        stat.onClick ? 'hover:bg-white/5 active:scale-[0.98]' : ''
                                    } ${index === 2 ? 'rounded-r-3xl' : ''} ${index === 0 ? 'rounded-l-3xl' : ''}`}
                                >
                                    <span className="text-xl font-bold">{stat.value}</span>
                                    <span className="text-xs uppercase tracking-wider text-white/60">{stat.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 w-full">
                        {isOwnProfile ? (
                            <div className="flex w-full gap-3">
                                <button
                                    onClick={() => window.dispatchEvent(new CustomEvent('open-profile-editor'))}
                                    className="flex-1 rounded-full bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] px-5 py-3 text-base font-bold text-white shadow-[0_10px_28px_rgba(255,0,0,0.35)] ring-1 ring-red-500/30 active:scale-[0.98] transition"
                                    style={{ textShadow: '0 0 12px rgba(255,255,255,0.25)' }}
                                >
                                    Edit Profile
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            const link = `${window.location.origin}/profile/${uid}`;
                                            await navigator.clipboard.writeText(link);
                                            alert('Profile link copied');
                                        } catch (e) {
                                            console.error(e);
                                        }
                                    }}
                                    className="flex-1 rounded-full bg-white px-5 py-3 text-base font-semibold text-black shadow-[0_6px_20px_rgba(0,0,0,0.25)] active:scale-[0.98] transition"
                                >
                                    Share Profile
                                </button>
                            </div>
                        ) : (
                            <div className="flex w-full gap-3">
                                <div className="flex-1">
                                    <FollowButton targetUid={uid} />
                                </div>
                                <button
                                    onClick={() => navigate(`/messages?to=${uid}`)}
                                    title="Message"
                                    className="flex-1 rounded-full bg-white/10 px-5 py-3 text-base font-semibold text-white hover:bg-white/15 transition"
                                >
                                    Send Message
                                </button>
                            </div>
                        )}
                    </div>

                    {!isArtist && (
                        <div className="mt-6 w-full rounded-3xl bg-white/5 ring-1 ring-white/10 p-4 shadow-lg">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 text-white">
                                        <span className="text-lg">💎</span>
                                        <span className="font-semibold">
                                            {user?.selectedBadgeTitle || `Supporter of ${userBadges[0]?.artistName || 'Artists'}`}
                                        </span>
                                    </div>
                                    {userBadges.length > 0 ? (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {userBadges.slice(0, 6).map((badge) => (
                                                <span
                                                    key={badge.artistId}
                                                    className="px-3 py-1 rounded-full bg-white/10 text-xs text-white/80"
                                                >
                                                    {badge.badgeTitle}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="mt-3 text-sm text-white/50">
                                            Tip your favourite artists to start collecting badges.
                                        </p>
                                    )}
                                </div>
                                <div className="min-w-[120px] flex flex-col items-end gap-1 text-right">
                                    <span className="text-xs uppercase tracking-wider text-white/40">
                                        Supporter Level
                                    </span>
                                    <span className="text-3xl font-bold text-white">
                                        L{supporterLevel}
                                    </span>
                                    <span className="text-xs text-white/60">
                                        {Number(totalTipsGiven || 0).toLocaleString()} tips given
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {isArtist && (
                        <div className="mt-6 w-full rounded-3xl bg-white/5 ring-1 ring-white/10 p-4 flex items-center justify-between">
                            {[
                                { label: 'Crown', value: artistBadgeCounts.crown, emoji: '👑' },
                                { label: 'Diamond', value: artistBadgeCounts.diamond, emoji: '💎' },
                                { label: 'Gold', value: artistBadgeCounts.gold, emoji: '🥇' },
                                { label: 'Silver', value: artistBadgeCounts.silver, emoji: '🥈' },
                                { label: 'Bronze', value: artistBadgeCounts.bronze, emoji: '🥉' },
                            ].map((badge) => (
                                <div key={badge.label} className="flex flex-col items-center text-xs">
                                    <span className="text-lg">{badge.emoji}</span>
                                    <span className="mt-1 font-semibold">{badge.value}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-8 w-full rounded-3xl bg-white/5 ring-1 ring-white/10 p-4 shadow-lg">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold">Recent Tips</h2>
                            <span className="text-xs uppercase tracking-wider text-white/40">
                                {isArtist ? 'Received' : 'Given'}
                            </span>
                        </div>
                        <div className="space-y-3">
                            {(isArtist ? recentReceivedTips : recentGivenTips).length === 0 ? (
                                <div className="text-white/50 text-center py-6">
                                    {isArtist ? 'No tips received yet' : 'No tips yet'}
                                </div>
                            ) : (
                                (isArtist ? recentReceivedTips : recentGivenTips).slice(0, 3).map((entry) => (
                                    <div
                                        key={entry.tx.id}
                                        className="flex items-center justify-between rounded-2xl bg-black/20 px-4 py-3"
                                    >
                                        <div className="flex min-w-0 items-center gap-3 flex-1">
                                            <div className="h-9 w-9 shrink-0 rounded-full bg-neutral-800 flex items-center justify-center text-sm font-bold">
                                                {isArtist
                                                    ? (entry.fromName?.[0] || 'S').toUpperCase()
                                                    : '🎵'}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium text-white">
                                                    {isArtist
                                                        ? entry.fromName || 'Supporter'
                                                        : entry.song?.title || entry.song?.name || 'Unknown Song'}
                                                </div>
                                                <div className="text-xs text-white/60 truncate">
                                                    {isArtist
                                                        ? entry.song?.title || entry.song?.name || 'Song'
                                                        : entry.song?.userName ||
                                                          entry.song?.artist ||
                                                          'Unknown Artist'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ml-3 shrink-0 text-right font-semibold text-white">
                                            💎 {Number(entry.tx.amount || 0)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {isArtist && (
                        <div className="mt-8 w-full rounded-3xl bg-white/5 ring-1 ring-white/10 p-4 shadow-lg">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-lg font-semibold">Songs</h2>
                                <span className="text-xs uppercase tracking-wider text-white/40">
                                    latest
                                </span>
                            </div>
                            <div className="space-y-3">
                                {artistSongs.length === 0 ? (
                                    <div className="text-white/50 text-center py-6">No songs yet</div>
                                ) : (
                                    artistSongs.slice(0, 10).map((song) => (
                                        <button
                                            key={song.id}
                                            type="button"
                                            onClick={() => song.id && navigate(`/song/${song.id}`)}
                                            className="w-full flex items-center justify-between rounded-2xl bg-black/20 px-4 py-3 hover:bg-white/10 transition"
                                        >
                                            <div className="flex min-w-0 items-center gap-3 flex-1">
                                                <div className="h-10 w-10 shrink-0 rounded-lg bg-neutral-800 flex items-center justify-center overflow-hidden">
                                                    {song.coverImage || song.thumbnail ? (
                                                        <img
                                                            src={song.coverImage || song.thumbnail}
                                                            alt={song.title || song.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <span className="text-xl">🎵</span>
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1 text-left">
                                                    <div className="truncate font-medium text-white">
                                                        {song.title || song.name || 'Untitled Song'}
                                                    </div>
                                                    <div className="text-xs text-white/60 truncate">
                                                        {song.genre || 'Music'} • {song.duration || '0:00'}
                                                    </div>
                                                </div>
                                            </div>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                className="h-5 w-5 text-white/50"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Followers/Following Modals */}
            <FollowersFollowingModal
                isOpen={showFollowersModal}
                onClose={() => setShowFollowersModal(false)}
                userId={uid}
                type="followers"
                userData={user}
            />
            <FollowersFollowingModal
                isOpen={showFollowingModal}
                onClose={() => setShowFollowingModal(false)}
                userId={uid}
                type="following"
                userData={user}
            />
        </div>
    );
};

export default ListenerProfile;
